import React, { useMemo } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';
import { rangeStats, StatRows, fmtPct, pctColor } from './rangeStats';

const fmtUSD = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

function BitcoinDrawdownCard({ data, dateRange }) {
  const slice = useMemo(() => {
    const history = data?.history || [];
    if (!history.length) return [];
    if (!dateRange?.start || !dateRange?.end) return history;
    return history.filter((b) => b.date >= dateRange.start && b.date <= dateRange.end);
  }, [data, dateRange]);

  const stats = useMemo(() => rangeStats(slice, 'price', data?.current), [slice, data]);

  if (!data || data.current == null) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 h-full">
        <h3 className="text-sm font-semibold text-gray-300">Bitcoin</h3>
        <p className="text-gray-500 text-sm mt-2">Unavailable</p>
      </div>
    );
  }
  const { current, ath, ath_date, pct_off_ath } = data;
  const drawdownColor = pct_off_ath != null && pct_off_ath < -20 ? '#dc2626' : '#f59e0b';

  // Only draw the ATH reference line if it falls within the visible window
  const visibleMax = slice.length ? Math.max(...slice.map((d) => d.price)) : 0;
  const showAthLine = ath != null && ath <= visibleMax * 1.05;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 h-full flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-300">Bitcoin · BTC/USD</h3>
          <span className="text-3xl font-bold text-white">{fmtUSD(current)}</span>
          {stats && (
            <span className={`text-sm font-semibold ${pctColor(stats.changePct)}`}>
              Range {fmtPct(stats.changePct)}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">CoinGecko</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 flex-1">
        <div className="sm:w-44 flex-shrink-0">
          <StatRows stats={stats} fmtValue={fmtUSD} />
        </div>
        <div className="flex-1 min-h-[160px]">
          <ResponsiveContainer width="100%" height="100%" minHeight={160}>
            <AreaChart data={slice}>
              <defs>
                <linearGradient id="btcGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} minTickGap={60} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} domain={['dataMin', 'dataMax']} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => [fmtUSD(v), 'Price']}
              />
              {showAthLine && (
                <ReferenceLine y={ath} stroke="#9ca3af" strokeDasharray="3 3" label={{ value: `ATH ${fmtUSD(ath)}`, fill: '#9ca3af', fontSize: 10, position: 'insideTopRight' }} />
              )}
              <Area type="monotone" dataKey="price" stroke="#f59e0b" fill="url(#btcGrad)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="text-[10px] text-gray-500 mt-2">
        ATH {fmtUSD(ath)} ({ath_date || '—'}) ·{' '}
        <span style={{ color: drawdownColor }}>{fmtPct(pct_off_ath)} off</span>
        {' '}· ~1yr history (CoinGecko free tier)
      </div>
    </div>
  );
}

export default BitcoinDrawdownCard;
